/**
 * Admin Usage Dashboard - Phase H1.5.5
 * 
 * Platform admin dashboard for monitoring organization usage:
 * - Organization usage table with sorting/filtering
 * - Usage percentage visualization with progress bars
 * - Anomaly alerts sidebar
 * - Override management modal
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, AlertTriangle, Settings2, Building2,
  TrendingUp, TrendingDown, Minus, Search, Filter,
  RefreshCw, ChevronDown, ChevronUp, X, Calendar,
  Loader2, AlertCircle, CheckCircle, Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Tier badge styling
const tierBadgeStyles = {
  a_free: 'bg-slate-100 text-slate-700 border-slate-300',
  a_pro: 'bg-blue-50 text-blue-700 border-blue-300',
  b_dedicated: 'bg-purple-50 text-purple-700 border-purple-300',
  c_onprem: 'bg-emerald-50 text-emerald-700 border-emerald-300',
};

const tierDisplayNames = {
  a_free: 'Free',
  a_pro: 'Pro',
  b_dedicated: 'Dedicated',
  c_onprem: 'On-Prem',
};

// Usage status indicator
const UsageIndicator = ({ percent, isUnlimited }) => {
  if (isUnlimited) {
    return <span className="text-slate-400 text-sm">Unlimited</span>;
  }
  
  let color = 'bg-emerald-500';
  let textColor = 'text-emerald-600';
  
  if (percent >= 90) {
    color = 'bg-red-500';
    textColor = 'text-red-600';
  } else if (percent >= 75) {
    color = 'bg-amber-500';
    textColor = 'text-amber-600';
  } else if (percent >= 50) {
    color = 'bg-blue-500';
    textColor = 'text-blue-600';
  }
  
  return (
    <div className="flex items-center gap-2">
      <Progress value={percent} className={`h-2 w-24 ${color}`} />
      <span className={`text-sm font-medium ${textColor}`}>{percent}%</span>
    </div>
  );
};

// Organization row component
const OrgUsageRow = ({ org, onOverrideClick, onFeaturesClick, t }) => {
  const [expanded, setExpanded] = useState(false);
  
  const highestUsage = Object.entries(org.percent_of_limit || {})
    .filter(([, val]) => val !== null)
    .sort(([, a], [, b]) => b - a)[0];
  
  const hasHighUsage = highestUsage && highestUsage[1] >= 80;
  
  return (
    <>
      <tr 
        className={`border-b border-slate-100 hover:bg-slate-50/50 cursor-pointer ${hasHighUsage ? 'bg-red-50/30' : ''}`}
        onClick={() => setExpanded(!expanded)}
        data-testid={`org-row-${org.org_id}`}
      >
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
            <Building2 className="h-4 w-4 text-slate-400" />
            <span className="font-medium text-slate-800">{org.org_name}</span>
            {hasHighUsage && <AlertTriangle className="h-4 w-4 text-amber-500" />}
          </div>
        </td>
        <td className="py-3 px-4">
          <Badge className={`${tierBadgeStyles[org.deployment_tier] || tierBadgeStyles.a_free} border`}>
            {tierDisplayNames[org.deployment_tier] || org.deployment_tier}
          </Badge>
        </td>
        <td className="py-3 px-4">
          <UsageIndicator 
            percent={org.percent_of_limit?.missions} 
            isUnlimited={org.limits?.missions_per_month === null}
          />
        </td>
        <td className="py-3 px-4">
          <UsageIndicator 
            percent={org.percent_of_limit?.storage} 
            isUnlimited={org.limits?.storage_bytes_max === null}
          />
        </td>
        <td className="py-3 px-4">
          <UsageIndicator 
            percent={org.percent_of_limit?.ai_calls} 
            isUnlimited={org.limits?.ai_calls_per_month === null}
          />
        </td>
        <td className="py-3 px-4 text-sm text-slate-500">
          {org.last_activity_at ? new Date(org.last_activity_at).toLocaleDateString() : '-'}
        </td>
        <td className="py-3 px-4">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onFeaturesClick(org);
            }}
            title="Feature Flags"
            data-testid={`features-btn-${org.org_id}`}
          >
            <Shield className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onOverrideClick(org);
            }}
            data-testid={`override-btn-${org.org_id}`}
          >
            <Settings2 className="h-4 w-4" />
          </Button>
        </td>
      </tr>
      
      {expanded && (
        <tr className="bg-slate-50/50">
          <td colSpan={7} className="px-4 py-4">
            <div className="grid grid-cols-3 gap-6">
              <div>
                <h4 className="text-sm font-medium text-slate-600 mb-2">{t.adminUsage?.resourceUsage || 'Resource Usage'}</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{t.adminUsage?.missions || 'Missions'}</span>
                    <span className="font-medium">{org.current_month?.missions || 0} / {org.limits?.missions_per_month ?? '∞'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{t.adminUsage?.pilots || 'Pilots'}</span>
                    <span className="font-medium">{org.current_month?.pilots || 0} / {org.limits?.pilots_max ?? '∞'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{t.adminUsage?.documents || 'Documents'}</span>
                    <span className="font-medium">{org.current_month?.documents || 0} / {org.limits?.documents_max ?? '∞'}</span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-slate-600 mb-2">{t.adminUsage?.apiUsage || 'API Usage'}</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{t.adminUsage?.apiCalls || 'API Calls/hr'}</span>
                    <span className="font-medium">{org.current_month?.api_calls || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{t.adminUsage?.aiCalls || 'AI Calls'}</span>
                    <span className="font-medium">{org.current_month?.ai_calls || 0}</span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-slate-600 mb-2">{t.adminUsage?.storage || 'Storage'}</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{t.adminUsage?.used || 'Used'}</span>
                    <span className="font-medium">{formatBytes(org.current_month?.storage_bytes || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{t.adminUsage?.limit || 'Limit'}</span>
                    <span className="font-medium">{org.limits?.storage_bytes_max ? formatBytes(org.limits.storage_bytes_max) : '∞'}</span>
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// Override Modal
const OverrideModal = ({ org, isOpen, onClose, onSave, t }) => {
  const [resource, setResource] = useState('missions');
  const [newLimit, setNewLimit] = useState('');
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      // Reset form
      setResource('missions');
      setNewLimit('');
      setReason('');
      // Default to 30 days from now
      const defaultExpiry = new Date();
      defaultExpiry.setDate(defaultExpiry.getDate() + 30);
      setExpiresAt(defaultExpiry.toISOString().split('T')[0]);
    }
  }, [isOpen]);
  
  const handleSave = async () => {
    if (!newLimit || !reason || reason.length < 10) {
      toast.error(t.adminUsage?.overrideValidation || 'Please fill all fields. Reason must be at least 10 characters.');
      return;
    }
    
    setSaving(true);
    try {
      await onSave({
        resource,
        new_limit: parseInt(newLimit),
        reason,
        expires_at: new Date(expiresAt).toISOString()
      });
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to create override');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="override-modal">
        <DialogHeader>
          <DialogTitle>{t.adminUsage?.createOverride || 'Create Override'}</DialogTitle>
          <DialogDescription>
            {org?.org_name} ({tierDisplayNames[org?.deployment_tier] || org?.deployment_tier})
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium text-slate-700">
              {t.adminUsage?.resource || 'Resource'}
            </label>
            <Select value={resource} onValueChange={setResource}>
              <SelectTrigger data-testid="override-resource-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="missions">{t.adminUsage?.missions || 'Missions'}</SelectItem>
                <SelectItem value="pilots">{t.adminUsage?.pilots || 'Pilots'}</SelectItem>
                <SelectItem value="documents">{t.adminUsage?.documents || 'Documents'}</SelectItem>
                <SelectItem value="storage">{t.adminUsage?.storage || 'Storage (bytes)'}</SelectItem>
                <SelectItem value="ai_calls">{t.adminUsage?.aiCalls || 'AI Calls'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-700">
              {t.adminUsage?.newLimit || 'New Limit'}
            </label>
            <Input 
              type="number" 
              value={newLimit} 
              onChange={(e) => setNewLimit(e.target.value)}
              placeholder="200"
              data-testid="override-new-limit"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-700">
              {t.adminUsage?.reason || 'Reason'} <span className="text-slate-400">(min 10 chars)</span>
            </label>
            <Textarea 
              value={reason} 
              onChange={(e) => setReason(e.target.value)}
              placeholder={t.adminUsage?.reasonPlaceholder || 'Customer requested temporary increase for project X'}
              data-testid="override-reason"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-700">
              {t.adminUsage?.expiresAt || 'Expires At'}
            </label>
            <Input 
              type="date" 
              value={expiresAt} 
              onChange={(e) => setExpiresAt(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              data-testid="override-expires"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t.common?.cancel || 'Cancel'}
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="override-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t.adminUsage?.saveOverride || 'Save Override'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Format bytes to human readable
const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Main Component
const AdminUsagePage = () => {
  const { t } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [orgs, setOrgs] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [tierFilter, setTierFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('highest_percent');
  
  // Modal
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  
  // Navigate to features page
  const handleFeaturesClick = (org) => {
    navigate(`/admin/orgs/${org.org_id}/features`);
  };
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // Fetch orgs usage
      let url = `${API_BASE}/api/admin/usage/orgs?sort_by=${sortBy}`;
      if (tierFilter !== 'all') {
        url += `&tier=${tierFilter}`;
      }
      
      const orgsRes = await fetch(url, { headers });
      if (!orgsRes.ok) {
        if (orgsRes.status === 403) {
          throw new Error('Access denied. Platform admin role required.');
        }
        throw new Error('Failed to fetch organization data');
      }
      const orgsData = await orgsRes.json();
      setOrgs(orgsData.organizations || []);
      
      // Fetch anomalies
      const anomaliesRes = await fetch(`${API_BASE}/api/admin/usage/anomalies`, { headers });
      if (anomaliesRes.ok) {
        const anomaliesData = await anomaliesRes.json();
        setAnomalies(anomaliesData.anomalies || []);
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, tierFilter, sortBy]);
  
  useEffect(() => {
    if (user?.role === 'platform_admin') {
      fetchData();
    }
  }, [fetchData, user]);
  
  const handleCreateOverride = async (overrideData) => {
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    
    const res = await fetch(
      `${API_BASE}/api/admin/usage/orgs/${selectedOrg.org_id}/override`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(overrideData)
      }
    );
    
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail?.message_en || 'Failed to create override');
    }
    
    toast.success(t.adminUsage?.overrideCreated || 'Override created successfully');
    fetchData(); // Refresh data
  };
  
  // Filter orgs by search
  const filteredOrgs = orgs.filter(org => 
    org.org_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    org.org_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Access check
  if (user?.role !== 'platform_admin') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-800">
                {t.adminUsage?.accessDenied || 'Access Denied'}
              </h3>
              <p className="text-slate-500 mt-2">
                {t.adminUsage?.platformAdminOnly || 'This page is only accessible to platform administrators.'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="space-y-6 p-6" data-testid="admin-usage-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            {t.adminUsage?.title || 'Platform Usage Dashboard'}
          </h1>
          <p className="text-slate-500 mt-1">
            {t.adminUsage?.subtitle || 'Monitor organization usage and manage resource limits'}
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {t.common?.refresh || 'Refresh'}
        </Button>
      </div>
      
      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t.adminUsage?.totalOrgs || 'Total Organizations'}</p>
                <p className="text-2xl font-bold text-slate-800">{orgs.length}</p>
              </div>
              <Building2 className="h-8 w-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t.adminUsage?.highUsage || 'High Usage (>80%)'}</p>
                <p className="text-2xl font-bold text-amber-600">
                  {orgs.filter(o => {
                    const maxPercent = Math.max(...Object.values(o.percent_of_limit || {}).filter(v => v !== null));
                    return maxPercent >= 80;
                  }).length}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-amber-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t.adminUsage?.anomalies || 'Anomalies'}</p>
                <p className="text-2xl font-bold text-red-600">{anomalies.length}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t.adminUsage?.unlimitedTier || 'Unlimited Tier'}</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {orgs.filter(o => o.deployment_tier === 'c_onprem').length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-emerald-300" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Anomalies Alert */}
      {anomalies.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-5 w-5" />
              {t.adminUsage?.anomalyAlerts || 'Anomaly Alerts'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {anomalies.slice(0, 5).map((anomaly, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm bg-white rounded-lg p-3 border border-amber-100">
                  <div>
                    <span className="font-medium text-slate-800">{anomaly.org_name}</span>
                    <span className="text-slate-500 ml-2">
                      {anomaly.metric}: {anomaly.ratio}x baseline
                    </span>
                  </div>
                  <Badge variant={anomaly.severity === 'critical' ? 'destructive' : 'secondary'}>
                    {anomaly.severity}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input 
                  placeholder={t.adminUsage?.searchOrgs || 'Search organizations...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            <Select value={tierFilter} onValueChange={setTierFilter}>
              <SelectTrigger className="w-[150px]" data-testid="tier-filter">
                <SelectValue placeholder={t.adminUsage?.allTiers || 'All Tiers'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.adminUsage?.allTiers || 'All Tiers'}</SelectItem>
                <SelectItem value="a_free">Free</SelectItem>
                <SelectItem value="a_pro">Pro</SelectItem>
                <SelectItem value="b_dedicated">Dedicated</SelectItem>
                <SelectItem value="c_onprem">On-Prem</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]" data-testid="sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="highest_percent">{t.adminUsage?.sortHighestUsage || 'Highest Usage'}</SelectItem>
                <SelectItem value="api_calls">{t.adminUsage?.sortApiCalls || 'API Calls'}</SelectItem>
                <SelectItem value="ai_calls">{t.adminUsage?.sortAiCalls || 'AI Calls'}</SelectItem>
                <SelectItem value="storage">{t.adminUsage?.sortStorage || 'Storage'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Main Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <p className="text-red-600">{error}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="usage-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">
                      {t.adminUsage?.organization || 'Organization'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">
                      {t.adminUsage?.tier || 'Tier'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">
                      {t.adminUsage?.missions || 'Missions'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">
                      {t.adminUsage?.storage || 'Storage'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">
                      {t.adminUsage?.aiCalls || 'AI Calls'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">
                      {t.adminUsage?.lastActivity || 'Last Activity'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">
                      {t.adminUsage?.actions || 'Actions'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrgs.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-slate-500">
                        {t.adminUsage?.noOrgsFound || 'No organizations found'}
                      </td>
                    </tr>
                  ) : (
                    filteredOrgs.map((org) => (
                      <OrgUsageRow
                        key={org.org_id}
                        org={org}
                        onOverrideClick={(o) => {
                          setSelectedOrg(o);
                          setShowOverrideModal(true);
                        }}
                        onFeaturesClick={handleFeaturesClick}
                        t={t}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Override Modal */}
      <OverrideModal
        org={selectedOrg}
        isOpen={showOverrideModal}
        onClose={() => setShowOverrideModal(false)}
        onSave={handleCreateOverride}
        t={t}
      />
    </div>
  );
};

export default AdminUsagePage;
